import { useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';

import { Stack } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { Card, Link, Checkbox, Typography, CardContent, useMediaQuery } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useDeleteMeetingMutation } from 'src/services/meetings/meetings.service';
import { IMeeting, MeetingType, MeetingStatus, MeetingPlatform } from 'src/services/meetings/meetings.types';

import Iconify from 'src/components/iconify';
import DateDisplay from 'src/components/date/date-display';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import MeetingEditorModal from 'src/sections/meetings/modal/meeting-editor-modal';
import { MeetingStatusLabel } from 'src/sections/meetings/components/meeting-status-label';
import { MeetingPlatformIcon } from 'src/sections/meetings/components/meeting-platform-icon';

// ----------------------------------------------------------------------
type Props = {
  row: IMeeting;
  selected?: boolean;
  onSelectRow?: VoidFunction;
};

export default function MeetingTableRow({
  row,
  selected,
  onSelectRow
}: Props) {

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const popover = usePopover();

  const { t } = useTranslate();

  const editMeetingControl = useBoolean();

  const router = useRouter();

  const confirmDelete = useBoolean();

  const [deleteMeeting, { isLoading: isDeletingMeeting }] = useDeleteMeetingMutation();

  const getViewDetailPath = useCallback((data: IMeeting) => paths.dashboard.notetaker.detail(data.id), []);

  const viewDetail = useCallback((data: IMeeting) => {
    router.push(getViewDetailPath(data));
  }, [router, getViewDetailPath]);

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const getPlatformLabel = (platform: MeetingPlatform): string => {
    switch (platform) {
      case MeetingPlatform.NATIVE:
        return 'Native';
      case MeetingPlatform.GOOGLE_MEET:
        return 'Google Meet';
      case MeetingPlatform.MICROSOFT_TEAMS:
        return 'Microsoft Teams';
      case MeetingPlatform.ZOOM:
        return 'Zoom';
      default:
        return platform;
    }
  };

  const onDelete = async () => {
    try {
      await deleteMeeting(row.id).unwrap();

      confirmDelete.onFalse();

      enqueueSnackbar(t('meetings.api.delete.success'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('meetings.api.delete.default_error'), { variant: 'error' });
    }
  };

  const onEditMeeting = useCallback((meetingId: string) => {
    editMeetingControl.onTrue();
  }, [editMeetingControl]);

  if (isMobile) {
    return (
      <Card sx={{ mx: 2, mb: 1 }}>
        <CardContent sx={{ position: 'relative', pr: 6, cursor: 'pointer' }} onClick={() => viewDetail(row)}>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Iconify icon="mdi:calendar" width={20} />
              <Link
                href={getViewDetailPath(row)}
                style={{
                  color: theme.palette.text.primary,
                  textDecoration: 'none',
                }}
              >
                <TruncatedTextField
                  text={row.title}
                  mode="characters"
                  limit={25}
                  showMoreEnabled={false}
                  variant="caption"
                  fontWeight="bold"
                  gutterBottom
                />
              </Link>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Iconify icon="mdi:clock-outline" width={20} />
              <Typography variant="body2">
                <DateDisplay date={row.start_at} format="DD MMM YYYY, hh:mm A" variant='body2' />
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Iconify icon="mdi:timer-outline" width={20} />
              <Typography variant="body2">
                {formatDuration(row.duration_minutes)}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Iconify icon="mdi:video" width={20} />
              <Typography variant="body2">
                {getPlatformLabel(row.platform)}
              </Typography>
            </Stack>

            <MeetingStatusLabel status={row.status} />
          </Stack>

          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 50 }}
            color={popover.open ? 'primary' : 'default'}
            onClick={(e) => {
              e.stopPropagation();
              popover.onOpen(e);
            }}
          >
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </CardContent>

        <CustomPopover
          open={popover.open}
          onClose={popover.onClose}
          arrow="right-top"
          sx={{ width: 140 }}
        >
          <MenuItem
            onClick={() => {
              popover.onClose();
              viewDetail(row);
            }}
          >
            <Iconify icon="solar:eye-bold" />
            {t('common.view')}
          </MenuItem>
        </CustomPopover>
      </Card>
    );
  }

  return (
    <>
      <TableRow hover>
        {
          onSelectRow && (
            <TableCell padding="checkbox">
              <Checkbox checked={selected} onClick={onSelectRow} />
            </TableCell>
          )
        }

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Link
            component={RouterLink}
            noWrap
            color="inherit"
            href={getViewDetailPath(row)}
          >
            <TruncatedTextField
              text={row.title}
              mode="characters"
              limit={50}
              showMoreEnabled={false}
              variant="subtitle2"
              fontWeight="bold"
            />
          </Link>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2">
            <DateDisplay date={row.start_at} format="DD MMM YYYY, hh:mm A" variant='body2' />
          </Typography>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2">
            {
              row.type === MeetingType.HOSTED || row.status === MeetingStatus.COMPLETED ?
                formatDuration(row.duration_minutes)
                :
                '--'
            }
          </Typography>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <MeetingPlatformIcon platform={row.platform} />
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <MeetingStatusLabel status={row.status} />
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            viewDetail(row);
          }}
        >
          <Iconify icon="solar:eye-bold" />
          {t('common.view')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            popover.onClose();
            onEditMeeting(row.id);
          }}
        >
          <Iconify icon="material-symbols:edit" />
          {t('meetings.actions.edit')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            popover.onClose();
            confirmDelete.onTrue();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="material-symbols:delete" />
          {t('meetings.actions.delete')}
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirmDelete.value}
        onClose={confirmDelete.onFalse}
        title={t('meetings.delete_confirm.title')}
        content={t('meetings.delete_confirm.content')}
        action={
          <LoadingButton variant="contained" color="error" onClick={onDelete} loading={isDeletingMeeting}>
            {t('common.delete')}
          </LoadingButton>
        }
      />

      {
        editMeetingControl.value && (
          <MeetingEditorModal open={editMeetingControl.value} meetingId={row.id} onClose={editMeetingControl.onFalse} />
        )
      }

    </>
  );
}